import Component from '@glimmer/component';
import { action } from '@ember/object';
import { on } from '@ember/modifier';
import { array } from '@ember/helper';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import type { S3ObjectSummary } from 'scaleflow/utils/aws-clients/s3VDRClient';
import {
  formatBytes,
  formatUploadedAt,
} from 'scaleflow/utils/manual-repo-archives';
import DeleteIcon from 'scaleflow/components/tailwind-icons/trash';
import DocumentText from 'scaleflow/components/tailwind-icons/document-text';
import RefreshCw from 'scaleflow/components/tailwind-icons/refresh-cw';

export interface ManualUploadsArchiveListSignature {
  Element: HTMLDivElement;
  Args: {
    Named: {
      archives: S3ObjectSummary[];
      onDelete: (archives: S3ObjectSummary[]) => void;
      isLoading?: boolean;
      /** Keys currently being deleted, so only those rows show a spinner. */
      deletingKeys?: string[];
    };
  };
}

type Row = {
  key: string;
  name: string;
  sizeText: string;
  uploadedText: string;
  isDeleting: boolean;
  isSelected: boolean;
  archive: S3ObjectSummary;
};

/**
 * The library listing, and the only place an archive can be deleted.
 *
 * Everything shown comes from the single `ListObjectsV2` call that loaded the
 * page — no per-row requests, and deliberately no "used by N X-Rays" column,
 * which would mean loading every scan and matching on filename.
 */
export default class ManualUploadsArchiveList extends Component<ManualUploadsArchiveListSignature> {
  @tracked selected = new Set<string>();

  get rows(): Row[] {
    const deleting = new Set(this.args.deletingKeys ?? []);
    return (
      [...(this.args.archives ?? [])]
        // Newest first: the archive a user just uploaded is the one they are
        // looking for.
        .sort((a, b) => {
          const at = a.lastModified?.getTime() ?? 0;
          const bt = b.lastModified?.getTime() ?? 0;
          return bt - at;
        })
        .map((archive) => ({
          key: archive.key,
          name: archive.name,
          sizeText: formatBytes(archive.size),
          uploadedText: formatUploadedAt(archive.lastModified),
          isDeleting: deleting.has(archive.key),
          // Read against the current rows, so a key whose archive has since
          // been deleted is never counted.
          isSelected: this.selected.has(archive.key),
          archive,
        }))
    );
  }

  get selectedArchives(): S3ObjectSummary[] {
    return this.rows.filter((row) => row.isSelected).map((row) => row.archive);
  }

  get allSelected(): boolean {
    return (
      this.rows.length > 0 && this.selectedArchives.length === this.rows.length
    );
  }

  get someSelected(): boolean {
    return this.selectedArchives.length > 0 && !this.allSelected;
  }

  get isBusy(): boolean {
    return Boolean(this.args.deletingKeys?.length);
  }

  @action
  toggle(key: string) {
    // Reassigned rather than mutated: a Set mutated in place is not tracked.
    const next = new Set(this.selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    this.selected = next;
  }

  @action
  toggleAll() {
    this.selected = this.allSelected
      ? new Set()
      : new Set(this.rows.map((row) => row.key));
  }

  @action
  deleteSelected() {
    const archives = this.selectedArchives;
    // Cleared up front: once handed over, a cancelled modal is cheap to redo,
    // and a stale selection would silently re-tick a re-uploaded filename.
    this.selected = new Set();
    this.args.onDelete(archives);
  }

  <template>
    <div
      class="rounded-lg border bg-white"
      data-test-archive-list
      ...attributes
    >
      <div class="px-6 py-4 border-b flex items-center justify-between gap-3">
        <h2 class="font-semibold text-gray-900">
          {{t "manual-uploads.library-title"}}
        </h2>
        <div class="flex items-center gap-3">
          {{#if @isLoading}}
            <RefreshCw class="w-4 h-4 animate-spin text-gray-400" />
          {{/if}}
          {{#if this.selectedArchives.length}}
            <button
              type="button"
              class="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:pointer-events-none"
              data-test-delete-selected
              disabled={{this.isBusy}}
              {{on "click" this.deleteSelected}}
            >
              <DeleteIcon class="w-3.5 h-3.5" />
              {{t
                "manual-uploads.delete-selected"
                count=this.selectedArchives.length
              }}
            </button>
          {{/if}}
        </div>
      </div>

      {{#if this.rows.length}}
        <div class="max-h-96 overflow-auto">
          <table class="w-full">
            <thead class="sticky top-0 z-10 bg-white">
              <tr class="text-left text-xs font-medium text-gray-500 uppercase">
                <th class="pl-6 pr-0 py-3 w-0">
                  <input
                    type="checkbox"
                    class="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    aria-label={{t "manual-uploads.select-all"}}
                    data-test-archive-select-all
                    checked={{this.allSelected}}
                    indeterminate={{this.someSelected}}
                    disabled={{this.isBusy}}
                    {{on "change" this.toggleAll}}
                  />
                </th>
                <th class="px-3 py-3">{{t "manual-uploads.library-name"}}</th>
                <th class="px-3 py-3">{{t "manual-uploads.library-size"}}</th>
                <th class="px-3 py-3">{{t
                    "manual-uploads.library-uploaded"
                  }}</th>
                <th class="px-6 py-3 text-right">
                  {{t "manual-uploads.library-actions"}}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              {{#each this.rows key="key" as |row|}}
                <tr class="hover:bg-gray-50" data-test-archive-row={{row.name}}>
                  <td class="pl-6 pr-0 py-3 w-0">
                    <input
                      type="checkbox"
                      class="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      aria-label={{t
                        "manual-uploads.select-archive"
                        name=row.name
                      }}
                      data-test-archive-checkbox={{row.name}}
                      checked={{row.isSelected}}
                      disabled={{row.isDeleting}}
                      {{on "change" (fn this.toggle row.key)}}
                    />
                  </td>
                  <td class="px-3 py-3">
                    <div class="flex items-center gap-3">
                      <div class="p-1.5 bg-gray-100 rounded-md shrink-0">
                        <DocumentText class="w-4 h-4 text-gray-500" />
                      </div>
                      <span
                        class="text-sm font-medium text-gray-900 break-all"
                      >{{row.name}}</span>
                    </div>
                  </td>
                  <td
                    class="px-3 py-3 text-sm text-gray-600 whitespace-nowrap"
                    data-test-archive-size
                  >{{row.sizeText}}</td>
                  <td
                    class="px-3 py-3 text-sm text-gray-600 whitespace-nowrap"
                    data-test-archive-uploaded
                  >{{row.uploadedText}}</td>
                  <td class="px-6 py-3 text-right">
                    <button
                      type="button"
                      class="inline-flex items-center justify-center rounded-md p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                      aria-label={{t "manual-uploads.delete-archive"}}
                      data-test-delete-archive={{row.name}}
                      disabled={{row.isDeleting}}
                      {{on "click" (fn @onDelete (array row.archive))}}
                    >
                      {{#if row.isDeleting}}
                        <RefreshCw class="w-4 h-4 animate-spin" />
                      {{else}}
                        <DeleteIcon class="w-4 h-4" />
                      {{/if}}
                    </button>
                  </td>
                </tr>
              {{/each}}
            </tbody>
          </table>
        </div>
      {{else if @isLoading}}
        <div class="px-6 py-10 text-center" data-test-archive-list-loading>
          <RefreshCw class="w-5 h-5 animate-spin text-gray-400 mx-auto" />
        </div>
      {{else}}
        <div class="px-6 py-10 text-center" data-test-archive-list-empty>
          <p class="text-sm font-medium text-gray-900">
            {{t "manual-uploads.library-empty"}}
          </p>
          <p class="text-xs text-gray-500 mt-1">
            {{t "manual-uploads.library-empty-hint"}}
          </p>
        </div>
      {{/if}}
    </div>
  </template>
}
